import {
  Component,
  input,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  signal,
  effect,
  computed,
  PLATFORM_ID,
  Renderer2,
  Type,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { RenderizadorDinamicoComponent } from '../dynamic-renderer/renderizador-dinamico.component';
import { ServicioResolucionDatosSsr } from '@core/services/ssr/resolucion-datos-ssr.service';
import type { EstructuraPaginaModyo, AnalyticsPaginaModyo } from '@core/interfaces/pagina-modyo.interface';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-renderizador-pagina',
  standalone: true,
  imports: [RenderizadorDinamicoComponent],
  templateUrl: './renderizador-pagina.component.html',
  styleUrl: './renderizador-pagina.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenderizadorPaginaComponent implements OnInit {
  public readonly uidTipoPagina = input<string>('pages');

  public readonly slugPagina = input<string>('');

  public readonly registroComponentes = input<Map<string, Type<unknown>>>(new Map<string, Type<unknown>>());

  protected readonly datosPagina = signal<EstructuraPaginaModyo | null>(null);

  protected readonly estaCargando = signal<boolean>(true);

  protected readonly tieneError = signal<boolean>(false);

  protected readonly mensajeError = signal<string>('');

  protected readonly metadatosSeo = computed(() => {
    const datos = this.datosPagina();
    return datos?.seo ?? null;
  });

  protected readonly configuracionPagina = computed(() => {
    const datos = this.datosPagina();
    return datos?.configuracion ?? null;
  });

  protected readonly analyticsPagina = computed(() => {
    const datos = this.datosPagina();
    return datos?.analytics ?? null;
  });

  protected readonly tituloPagina = computed(() => {
    const datos = this.datosPagina();
    return datos?.seo?.titulo ?? datos?.meta?.titulo ?? '';
  });

  protected readonly componentesPagina = computed(() => {
    const datos = this.datosPagina();
    return datos?.componentes ?? [];
  });

  private readonly resolucionSsr = inject(ServicioResolucionDatosSsr);
  private readonly servicioTitulo = inject(Title);
  private readonly servicioMeta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly route = inject(ActivatedRoute);
  private readonly esNavegador = isPlatformBrowser(this.platformId);

  constructor() {
    this.registrarEffectActualizarTitulo();
    this.registrarEffectActualizarDescripcion();
    this.registrarEffectActualizarPalabrasClave();
    this.registrarEffectActualizarMetadatosOg();
    this.registrarEffectActualizarCanonical();
    this.registrarEffectActualizarMetaTagsAdicionales();
  }

  public ngOnInit(): void {
    if (this.intentarInicializarDesdeResolver()) {
      return;
    }

    if (!this.validarSlugPagina()) {
      return;
    }

    void this.cargarDatosPagina();
  }

  private registrarEffectActualizarTitulo(): void {
    effect(() => {
      const titulo = this.tituloPagina();
      if (titulo) {
        this.servicioTitulo.setTitle(titulo);
      }
    });
  }

  private registrarEffectActualizarDescripcion(): void {
    effect(() => {
      const seo = this.metadatosSeo();
      if (seo?.descripcion) {
        this.servicioMeta.updateTag({ name: 'description', content: seo.descripcion });
      }
    });
  }

  private registrarEffectActualizarPalabrasClave(): void {
    effect(() => {
      const seo = this.metadatosSeo();
      if (seo?.palabrasClave && seo.palabrasClave.length) {
        this.servicioMeta.updateTag({ name: 'keywords', content: seo.palabrasClave.join(', ') });
      }
    });
  }

  private registrarEffectActualizarMetadatosOg(): void {
    effect(() => {
      const seo = this.metadatosSeo();
      if (!seo) {
        return;
      }

      if (seo.imagenOg) {
        this.servicioMeta.updateTag({ property: 'og:image', content: seo.imagenOg });
      }
      if (seo.titulo) {
        this.servicioMeta.updateTag({ property: 'og:title', content: seo.titulo });
      }
      if (seo.descripcion) {
        this.servicioMeta.updateTag({ property: 'og:description', content: seo.descripcion });
      }
      if (seo.ogTipo) {
        this.servicioMeta.updateTag({ property: 'og:type', content: seo.ogTipo });
      }
    });

    effect(() => {
      const seo = this.metadatosSeo();
      if (seo?.robotsMeta) {
        this.servicioMeta.updateTag({ name: 'robots', content: seo.robotsMeta });
      }
    });
  }

  private registrarEffectActualizarMetaTagsAdicionales(): void {
    effect(() => {
      const seo = this.metadatosSeo();
      if (!seo?.metaTags?.length) {
        return;
      }
      seo.metaTags.forEach((tag) => {
        const attrType = tag.clave.startsWith('og:') ? 'property' : 'name';
        this.servicioMeta.updateTag({ [attrType]: tag.clave, content: tag.valor });
      });
    });
  }

  private registrarEffectActualizarCanonical(): void {
    effect(() => {
      const seo = this.metadatosSeo();
      if (seo?.canonical && this.esNavegador) {
        this.actualizarLinkCanonical(seo.canonical);
      }
    });

    effect(() => {
      const analytics = this.analyticsPagina();
      if (analytics && this.esNavegador) {
        this.enviarEventosAnalytics(analytics);
      }
    });
  }

  private intentarInicializarDesdeResolver(): boolean {
    const datosFeature = this.route.snapshot.data['datosPagina'] as EstructuraPaginaModyo | null;

    if (datosFeature) {
      this.datosPagina.set(datosFeature);
      this.aplicarSeoSincrono(datosFeature);
      this.estaCargando.set(false);
      return true;
    }

    const datosDes = this.route.snapshot.data['contenidoModyo'] as EstructuraPaginaModyo | null;

    if (datosDes) {
      this.datosPagina.set(datosDes);
      this.aplicarSeoSincrono(datosDes);
      this.estaCargando.set(false);
      return true;
    }

    return false;
  }

  private aplicarSeoSincrono(datos: EstructuraPaginaModyo): void {
    const titulo = datos.seo?.titulo ?? datos.meta?.titulo;
    if (titulo) {
      this.servicioTitulo.setTitle(titulo);
    }

    const seo = datos.seo;
    if (!seo) {
      return;
    }

    if (seo.descripcion) {
      this.servicioMeta.updateTag({ name: 'description', content: seo.descripcion });
      this.servicioMeta.updateTag({ property: 'og:description', content: seo.descripcion });
    }
    if (seo.palabrasClave?.length) {
      this.servicioMeta.updateTag({ name: 'keywords', content: seo.palabrasClave.join(', ') });
    }
    if (seo.titulo) {
      this.servicioMeta.updateTag({ property: 'og:title', content: seo.titulo });
    }
    if (seo.imagenOg) {
      this.servicioMeta.updateTag({ property: 'og:image', content: seo.imagenOg });
    }
    if (seo.ogTipo) {
      this.servicioMeta.updateTag({ property: 'og:type', content: seo.ogTipo });
    }
    if (seo.robotsMeta) {
      this.servicioMeta.updateTag({ name: 'robots', content: seo.robotsMeta });
    }
    if (seo.canonical) {
      this.actualizarLinkCanonical(seo.canonical);
    }
    seo.metaTags?.forEach((tag) => {
      const attrType = tag.clave.startsWith('og:') ? 'property' : 'name';
      this.servicioMeta.updateTag({ [attrType]: tag.clave, content: tag.valor });
    });
  }

  private validarSlugPagina(): boolean {
    if (this.slugPagina()) {
      return true;
    }

    /* Si no hay slug, solo mostrar loading (no error) - puede ser que se esté pre-renderizando */
    return false;
  }

  private async cargarDatosPagina(): Promise<void> {
    this.estaCargando.set(true);
    this.tieneError.set(false);

    try {
      const datos = await firstValueFrom(
        this.resolucionSsr.obtenerDatosPagina(this.uidTipoPagina(), this.slugPagina()),
      );
      this.datosPagina.set(datos);
    } catch {
      this.tieneError.set(true);
      this.mensajeError.set('No se pudo cargar el contenido de la página');
    } finally {
      this.estaCargando.set(false);
    }
  }

  private actualizarLinkCanonical(url: string): void {
    if (!this.esNavegador) {
      return;
    }

    const linkExistente = this.document.querySelector('link[rel="canonical"]');

    if (linkExistente) {
      this.renderer.setAttribute(linkExistente, 'href', url);
    } else {
      const link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.setAttribute(link, 'href', url);
      this.renderer.appendChild(this.document.head, link);
    }
  }

  private enviarEventosAnalytics(analytics: AnalyticsPaginaModyo): void {
    if (!this.esNavegador) {
      return;
    }

    const windowWithDataLayer = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    if (analytics.pageName && typeof windowWithDataLayer.dataLayer !== 'undefined') {
      windowWithDataLayer.dataLayer?.push({
        event: 'pageview',
        pageName: analytics.pageName,
        pageCategory: analytics.categoria,
        customEvents: analytics.eventosTracking,
      });
    }
  }
}
